import {
  useAppDispatch,
  useAppSelector,
} from "@/app/lib/features/redux/redux.hook";
import { useCallback, useMemo } from "react";
import {
  getProductReviews as getProductReviewsThunk,
  getProductReviewsList as getProductReviewsListThunk,
  getReviewVisuals as getReviewVisualsThunk,
  performReviewAction as performReviewActionThunk,
} from "./productReview.thunk";
import {
  resetProductReview,
  resetReviewVisuals,
  setInitialProductReview,
  setCurrentProductSlug,
  setPendingFilters,
  setAppliedFilters,
  syncFiltersFromUrl,
  toggleRatingFilter,
  toggleTopicFilter,
  setSellerFilter,
  setSearchFilter,
  setSortFilter,
  clearAllFilters,
  clearRatingFilter,
  clearTopicFilter,
  setShowFilterSidebar,
  setOpenFilterDropdown,
} from "./productReview.slice";
import { isLoading, isSuccess, isError } from "@/app/utils/asyncStatus";
import {
  ReviewProductData,
  ReviewSellerData,
  ReviewFilterData,
  ReviewFilterRowData,
  ReviewRatingStats,
  ReviewSearchData,
  ReviewInfoBoxData,
  ReviewAISummaryData,
  ReviewFilters,
  ProductReviewData,
  ReviewsListData,
} from "./productReview.types";

const useProductReview = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((store) => store.productReview);

  const productCardData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "product_summary")?.data as
        | ReviewProductData
        | undefined,
    [state.data],
  );

  const sellerInfoData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "seller_info")?.data as
        | ReviewSellerData
        | undefined,
    [state.data],
  );

  const filterGroupsData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "filter_groups")?.data as
        | ReviewFilterData
        | undefined,
    [state.data],
  );

  const filtersData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "filters")?.data as
        | ReviewFilterRowData
        | undefined,
    [state.data],
  );

  const ratingStatsData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "rating_stats")?.data as
        | ReviewRatingStats
        | undefined,
    [state.data],
  );

  const searchBarData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "search_bar")?.data as
        | ReviewSearchData
        | undefined,
    [state.data],
  );

  const infoBoxData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "info_box")?.data as
        | ReviewInfoBoxData
        | undefined,
    [state.data],
  );

  const aiSummaryData = useMemo(
    () =>
      state.data?.sections.find((s) => s.type === "ai_summary")?.data as
        | ReviewAISummaryData
        | undefined,
    [state.data],
  );

  const statusFlags = useMemo(
    () => ({
      isLoading: isLoading(state.status),
      isSuccess: isSuccess(state.status),
      isError: isError(state.status),
      isReviewsListLoading: isLoading(state.reviewsListStatus),
      isReviewsListSuccess: isSuccess(state.reviewsListStatus),
      isReviewVisualsLoading: isLoading(state.reviewVisualsStatus),
      isReviewVisualsSuccess: isSuccess(state.reviewVisualsStatus),
    }),
    [state.status, state.reviewsListStatus, state.reviewVisualsStatus],
  );

  const hasMore = useMemo(
    () =>
      state.reviewsList?.pagination
        ? state.reviewsList.pagination.current_page <
          state.reviewsList.pagination.last_page
        : false,
    [state.reviewsList],
  );

  const hasMoreVisuals = useMemo(
    () =>
      state.reviewVisuals?.pagination
        ? state.reviewVisuals.pagination.current_page <
          state.reviewVisuals.pagination.last_page
        : false,
    [state.reviewVisuals],
  );

  const visualsPhotos = useMemo(
    () => state.reviewVisuals?.items.flatMap((item) => item.images) ?? [],
    [state.reviewVisuals?.items],
  );

  const fetchProductReviews = useCallback(
    async (productSlug: string) => {
      const result = await dispatch(getProductReviewsThunk(productSlug));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchProductReviewsList = useCallback(
    async (productSlug: string, filters?: ReviewFilters) => {
      const result = await dispatch(
        getProductReviewsListThunk({ productSlug, filters }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchReviewVisuals = useCallback(
    async (productSlug: string, page: number = 1, perPage: number = 20) => {
      const result = await dispatch(
        getReviewVisualsThunk({ productSlug, page, perPage }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const performAction = useCallback(
    async (reviewId: number, actionType: "like" | "complaint") => {
      const result = await dispatch(
        performReviewActionThunk({ reviewId, actionType }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const loadMoreReviews = useCallback(async () => {
    if (!state.reviewsList?.pagination || !state.currentProductSlug)
      return false;

    const { current_page, last_page } = state.reviewsList.pagination;
    if (current_page >= last_page) return false;

    const result = await dispatch(
      getProductReviewsListThunk({
        productSlug: state.currentProductSlug,
        filters: {
          ...state.appliedFilters,
          page: current_page + 1,
          per_page: 15,
        },
      }),
    );
    return result.meta.requestStatus === "fulfilled";
  }, [
    dispatch,
    state.reviewsList,
    state.currentProductSlug,
    state.appliedFilters,
  ]);

  const loadMoreVisuals = useCallback(async () => {
    if (!state.reviewVisuals?.pagination || !state.currentProductSlug)
      return false;

    const { current_page, last_page } = state.reviewVisuals.pagination;
    if (current_page >= last_page) return false;

    const result = await dispatch(
      getReviewVisualsThunk({
        productSlug: state.currentProductSlug,
        page: current_page + 1,
        perPage: 20,
      }),
    );
    return result.meta.requestStatus === "fulfilled";
  }, [dispatch, state.reviewVisuals, state.currentProductSlug]);

  const updateCurrentSlug = useCallback(
    (slug: string) => {
      dispatch(setCurrentProductSlug(slug));
    },
    [dispatch],
  );

  /** SSR/ISR verisini Redux'a yükler (mount'ta bir kez) — bkz. setInitialProductReview. */
  const seedInitialReview = useCallback(
    (payload: {
      slug: string;
      data: ProductReviewData | null;
      reviewsList: ReviewsListData | null;
    }) => {
      dispatch(setInitialProductReview(payload));
    },
    [dispatch],
  );

  const updatePendingFilters = useCallback(
    (filters: ReviewFilters) => {
      dispatch(setPendingFilters(filters));
    },
    [dispatch],
  );

  const updateAppliedFilters = useCallback(
    (filters: ReviewFilters) => {
      dispatch(setAppliedFilters(filters));
    },
    [dispatch],
  );

  const syncFromUrl = useCallback(
    (filters: ReviewFilters) => {
      dispatch(syncFiltersFromUrl(filters));
    },
    [dispatch],
  );

  const toggleRating = useCallback(
    (rating: number) => {
      dispatch(toggleRatingFilter(rating));
    },
    [dispatch],
  );

  const toggleTopic = useCallback(
    (topic: string) => {
      dispatch(toggleTopicFilter(topic));
    },
    [dispatch],
  );

  const updateSellerFilter = useCallback(
    (sellerId: number | undefined) => {
      dispatch(setSellerFilter(sellerId));
    },
    [dispatch],
  );

  const updateSearchFilter = useCallback(
    (search: string | undefined) => {
      dispatch(setSearchFilter(search));
    },
    [dispatch],
  );

  const updateSortFilter = useCallback(
    (sort: string | undefined) => {
      dispatch(setSortFilter(sort));
    },
    [dispatch],
  );

  const clearFilters = useCallback(() => {
    dispatch(clearAllFilters());
  }, [dispatch]);

  const clearRating = useCallback(() => {
    dispatch(clearRatingFilter());
  }, [dispatch]);

  const clearTopic = useCallback(() => {
    dispatch(clearTopicFilter());
  }, [dispatch]);

  const toggleFilterSidebar = useCallback(
    (show: boolean) => {
      dispatch(setShowFilterSidebar(show));
    },
    [dispatch],
  );

  const updateOpenDropdown = useCallback(
    (key: string | null) => {
      dispatch(setOpenFilterDropdown(key));
    },
    [dispatch],
  );

  const reset = useCallback(() => {
    dispatch(resetProductReview());
  }, [dispatch]);

  const resetVisuals = useCallback(() => {
    dispatch(resetReviewVisuals());
  }, [dispatch]);

  return {
    sections: state.data?.sections ?? [],
    reviewsList: state.reviewsList?.reviews ?? [],
    reviewVisuals: state.reviewVisuals?.items ?? [],
    visualsPhotos,
    pagination: state.reviewsList?.pagination,
    visualsPagination: state.reviewVisuals?.pagination,
    productCardData,
    sellerInfoData,
    filterGroupsData,
    filtersData,
    ratingStatsData,
    searchBarData,
    infoBoxData,
    aiSummaryData,
    hasMore,
    hasMoreVisuals,
    currentProductSlug: state.currentProductSlug,
    appliedFilters: state.appliedFilters,
    pendingFilters: state.pendingFilters,
    showFilterSidebar: state.showFilterSidebar,
    openFilterDropdowns: state.openFilterDropdowns,
    status: state.status,
    reviewsListStatus: state.reviewsListStatus,
    reviewVisualsStatus: state.reviewVisualsStatus,
    error: state.error,
    ...statusFlags,
    fetchProductReviews,
    fetchProductReviewsList,
    fetchReviewVisuals,
    performAction,
    loadMoreReviews,
    loadMoreVisuals,
    updateCurrentSlug,
    seedInitialReview,
    updatePendingFilters,
    updateAppliedFilters,
    syncFromUrl,
    toggleRating,
    toggleTopic,
    updateSellerFilter,
    updateSearchFilter,
    updateSortFilter,
    clearFilters,
    clearRating,
    clearTopic,
    toggleFilterSidebar,
    updateOpenDropdown,
    reset,
    resetVisuals,
  };
};

export default useProductReview;
